import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { ArrowLeft, Ban, Download, LogOut, Search, ShieldCheck, Trash2 } from "lucide-react";
import { Link } from "react-router-dom";
import { api, API } from "../lib/api";

const mono = "font-mono text-[10px] uppercase tracking-[0.2em]";
const SORTS = [
  ["joined", "Joined"],
  ["active", "Last active"],
  ["activity", "Most activity"],
];

const fmtDate = (s) => {
  if (!s) return "—";
  const d = new Date(s.length === 10 ? `${s}T00:00:00Z` : s);
  return Number.isNaN(d.getTime()) ? "—" : d.toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });
};
const fmtWhen = (s) => {
  if (!s) return "Never";
  const d = new Date(s.length === 10 ? `${s}T00:00:00Z` : s);
  if (Number.isNaN(d.getTime())) return "Never";
  const days = Math.floor((Date.now() - d.getTime()) / 86400000);
  if (days <= 0) return "Today";
  if (days === 1) return "Yesterday";
  if (days < 30) return `${days} days ago`;
  return fmtDate(s);
};

const Avatar = ({ reader, size = "w-9 h-9" }) => (
  reader.picture ? (
    <img src={reader.picture} alt="" referrerPolicy="no-referrer" className={`${size} rounded-full object-cover border border-line shrink-0`} />
  ) : (
    <span className={`${size} rounded-full border border-line flex items-center justify-center font-serif text-vellum-muted shrink-0`}>
      {(reader.name || reader.email || "?").trim().charAt(0).toUpperCase()}
    </span>
  )
);

const Confirm = ({ title, body, confirmLabel, danger, typed, expected, onTyped, onCancel, onConfirm, busy }) => (
  <div className="fixed inset-0 z-[120] bg-ink/90 flex items-center justify-center px-6" data-testid="reader-confirm">
    <div className="w-full max-w-lg border border-line bg-ink-800 p-8">
      <h3 className="font-serif text-2xl font-light text-vellum">{title}</h3>
      <p className="mt-4 text-sm text-vellum-muted leading-relaxed">{body}</p>
      {expected && (
        <div className="mt-6">
          <label className={`${mono} text-vellum-muted block mb-2`}>Type {expected} to confirm</label>
          <input
            value={typed}
            onChange={(e) => onTyped(e.target.value)}
            data-testid="reader-delete-confirm-input"
            autoFocus
            className="w-full bg-transparent border border-line px-4 py-3 text-vellum font-sans text-sm focus:border-crimson focus:outline-none"
          />
        </div>
      )}
      <div className="mt-8 flex gap-3 justify-end">
        <button onClick={onCancel} data-testid="reader-confirm-cancel" className={`${mono} px-5 py-3 text-vellum-muted hover:text-vellum transition-colors`}>Cancel</button>
        <button
          onClick={onConfirm}
          disabled={busy || (expected && typed.trim().toLowerCase() !== expected.toLowerCase())}
          data-testid="reader-confirm-go"
          className={`${mono} px-5 py-3 disabled:opacity-40 transition-colors ${danger ? "bg-crimson text-onaccent hover:bg-crimson-hover" : "bg-vellum text-ink hover:bg-brass"}`}
        >
          {busy ? "Working…" : confirmLabel}
        </button>
      </div>
    </div>
  </div>
);

const Stat = ({ label, value, testId }) => (
  <div className="border border-line px-5 py-4" data-testid={testId}>
    <p className={`${mono} text-vellum-muted`}>{label}</p>
    <p className="font-serif text-2xl font-light text-vellum mt-1">{value}</p>
  </div>
);

function ReaderDetail({ userId, onBack, onChanged }) {
  const [data, setData] = useState(null);
  const [confirm, setConfirm] = useState(null); // "signout" | "block" | "unblock" | "delete"
  const [typed, setTyped] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api.get(`/admin/readers/${userId}`).then((r) => setData(r.data)).catch(() => toast.error("Could not open that reader"));
  }, [userId]);
  useEffect(load, [load]);

  if (!data) return <p className={`${mono} text-vellum-muted`} data-testid="reader-detail-loading">Reading the record…</p>;
  const p = data.profile;

  const act = async () => {
    setBusy(true);
    try {
      if (confirm === "signout") {
        const r = await api.post(`/admin/readers/${userId}/signout`);
        toast.success(`Signed out of ${r.data.ended} ${r.data.ended === 1 ? "session" : "sessions"}`);
        load();
      } else if (confirm === "block" || confirm === "unblock") {
        await api.post(`/admin/readers/${userId}/block`, { blocked: confirm === "block" });
        toast.success(confirm === "block" ? "Account blocked" : "Account unblocked");
        load();
      } else if (confirm === "delete") {
        await api.delete(`/admin/readers/${userId}`, { params: { email: typed.trim() } });
        toast.success("Account and all its data deleted");
        onChanged();
        onBack();
        return;
      }
      onChanged();
    } catch (e) {
      toast.error(e.response?.data?.detail || "That did not work");
    } finally {
      setBusy(false);
      setConfirm(null);
      setTyped("");
    }
  };

  const copy = {
    signout: ["Sign out of all devices?", "Ends every active session immediately. Nothing is deleted — they simply sign in again.", "Sign out everywhere", false],
    block: ["Block this account?", "Their data stays intact, but any attempt to sign in is refused and their current sessions end at once. Reversible from here.", "Block account", true],
    unblock: ["Unblock this account?", "They will be able to sign in again, with everything as they left it.", "Unblock account", false],
    delete: ["Delete this account?", "Removes the account and everything attached to it — TBR, notes, progress and sessions. This cannot be undone.", "Delete permanently", true],
  }[confirm] || [];

  return (
    <div data-testid="reader-detail">
      <button onClick={onBack} data-testid="reader-back" className={`${mono} inline-flex items-center gap-2 text-vellum-muted hover:text-crimson transition-colors`}>
        <ArrowLeft className="w-3.5 h-3.5" /> All readers
      </button>

      <div className="mt-8 flex flex-col lg:flex-row lg:items-center gap-6 justify-between border-b border-line pb-8">
        <div className="flex items-center gap-5 min-w-0">
          <Avatar reader={p} size="w-16 h-16" />
          <div className="min-w-0">
            <h2 className="font-serif text-3xl font-light text-vellum truncate" data-testid="reader-detail-name">{p.name || "No name"}</h2>
            <p className="text-sm text-vellum-muted truncate" data-testid="reader-detail-email">{p.email}</p>
            <p className={`${mono} text-vellum-faint mt-2`}>
              Joined {fmtDate(p.created_at)} · Last active {fmtWhen(p.last_active)}
              {p.blocked && <span className="text-crimson"> · Blocked</span>}
            </p>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <button onClick={() => setConfirm("signout")} data-testid="reader-action-signout" className={`${mono} inline-flex items-center gap-2 border border-line px-4 py-3 text-vellum-muted hover:text-vellum hover:border-vellum-muted transition-colors`}>
            <LogOut className="w-3.5 h-3.5" /> Sign out everywhere
          </button>
          <a href={`${API}/admin/readers/${userId}/export`} data-testid="reader-action-export" className={`${mono} inline-flex items-center gap-2 border border-line px-4 py-3 text-vellum-muted hover:text-vellum hover:border-vellum-muted transition-colors`}>
            <Download className="w-3.5 h-3.5" /> Export data
          </a>
          <button onClick={() => setConfirm(p.blocked ? "unblock" : "block")} data-testid="reader-action-block" className={`${mono} inline-flex items-center gap-2 border border-line px-4 py-3 text-vellum-muted hover:text-vellum hover:border-vellum-muted transition-colors`}>
            {p.blocked ? <ShieldCheck className="w-3.5 h-3.5" /> : <Ban className="w-3.5 h-3.5" />} {p.blocked ? "Unblock" : "Block"}
          </button>
          <button onClick={() => setConfirm("delete")} data-testid="reader-action-delete" className={`${mono} inline-flex items-center gap-2 border border-crimson/60 px-4 py-3 text-crimson hover:bg-crimson hover:text-onaccent transition-colors`}>
            <Trash2 className="w-3.5 h-3.5" /> Delete
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mt-8">
        <Stat label="On the TBR" value={data.tbr.length} testId="reader-stat-tbr" />
        <Stat label="Notes written" value={data.notes.length} testId="reader-stat-notes" />
        <Stat label="Curriculums started" value={data.progress.length} testId="reader-stat-progress" />
        <Stat label="Signed in on" value={`${data.active_sessions} ${data.active_sessions === 1 ? "device" : "devices"}`} testId="reader-stat-sessions" />
      </div>

      <section className="mt-14" data-testid="reader-sessions">
        <h3 className="font-serif text-2xl font-light text-vellum mb-5">Sign-in sessions</h3>
        {data.sessions.length === 0 ? (
          <p className="text-sm text-vellum-muted">No sessions — not signed in anywhere.</p>
        ) : (
          <div className="border border-line divide-y divide-line">
            {data.sessions.map((s, i) => (
              <div key={i} className="px-5 py-4 flex items-center justify-between gap-4" data-testid={`reader-session-${i}`}>
                <p className="text-sm text-vellum">{s.device}</p>
                <p className={`${mono} text-vellum-muted`}>
                  {s.active ? "Active" : "Expired"} · started {fmtWhen(s.created_at)}
                </p>
              </div>
            ))}
          </div>
        )}
      </section>

      <section className="mt-14" data-testid="reader-tbr">
        <h3 className="font-serif text-2xl font-light text-vellum mb-5">Their TBR list</h3>
        {data.tbr.length === 0 ? (
          <p className="text-sm text-vellum-muted">Nothing on it yet.</p>
        ) : (
          <div className="border border-line divide-y divide-line">
            {data.tbr.map((t, i) => (
              <div key={t.item_id} className="px-5 py-4 flex items-center justify-between gap-4" data-testid={`reader-tbr-${i}`}>
                <Link to={`/library/${t.item_id}`} className="text-sm text-vellum hover:text-brass transition-colors truncate">
                  {t.item?.title || "Deleted entry"}
                  {t.item?.creator && <span className="text-vellum-muted"> · {t.item.creator}</span>}
                </Link>
                <p className={`${mono} text-vellum-faint shrink-0`}>{fmtWhen(t.added_at)}</p>
              </div>
            ))}
          </div>
        )}
      </section>

      <section className="mt-14" data-testid="reader-notes">
        <h3 className="font-serif text-2xl font-light text-vellum mb-2">Their notes</h3>
        <p className="text-sm text-vellum-muted italic mb-5">Written privately. Read only when there is a reason to.</p>
        {data.notes.length === 0 ? (
          <p className="text-sm text-vellum-muted">No notes written.</p>
        ) : (
          <div className="space-y-4">
            {data.notes.map((nt, i) => (
              <article key={nt.item_id} className="border border-line p-6" data-testid={`reader-note-${i}`}>
                <div className="flex items-baseline justify-between gap-4">
                  <Link to={`/library/${nt.item_id}`} className={`${mono} text-brass hover:text-crimson transition-colors`}>
                    {nt.item?.title || "Deleted entry"}
                  </Link>
                  <p className={`${mono} text-vellum-faint shrink-0`}>{fmtWhen(nt.updated_at)}</p>
                </div>
                <p className="mt-4 text-sm text-vellum leading-relaxed whitespace-pre-wrap">{nt.text}</p>
              </article>
            ))}
          </div>
        )}
      </section>

      <section className="mt-14" data-testid="reader-progress">
        <h3 className="font-serif text-2xl font-light text-vellum mb-5">Curriculum progress</h3>
        {data.progress.length === 0 ? (
          <p className="text-sm text-vellum-muted">Nothing started.</p>
        ) : (
          <div className="space-y-4">
            {data.progress.map((pr, i) => (
              <article key={pr.curriculum_id} className="border border-line p-6" data-testid={`reader-progress-${i}`}>
                <div className="flex items-baseline justify-between gap-4">
                  <Link to={`/curriculums/${pr.curriculum_id}`} className="font-serif text-xl font-light text-vellum hover:text-brass transition-colors">{pr.title}</Link>
                  <p className={`${mono} text-vellum-muted shrink-0`}>
                    {pr.completed.length}{pr.steps ? ` / ${pr.steps}` : ""} steps · {fmtWhen(pr.updated_at)}
                  </p>
                </div>
                {pr.completed_steps.length > 0 && (
                  <ul className="mt-4 space-y-1.5">
                    {pr.completed_steps.map((s) => (
                      <li key={s.index} className="text-sm text-vellum-muted">
                        <span className={`${mono} text-vellum-faint mr-3`}>{String(s.index + 1).padStart(2, "0")}</span>{s.label}
                      </li>
                    ))}
                  </ul>
                )}
              </article>
            ))}
          </div>
        )}
      </section>

      {confirm && (
        <Confirm
          title={copy[0]}
          body={copy[1]}
          confirmLabel={copy[2]}
          danger={copy[3]}
          expected={confirm === "delete" ? p.email : null}
          typed={typed}
          onTyped={setTyped}
          onCancel={() => { setConfirm(null); setTyped(""); }}
          onConfirm={act}
          busy={busy}
        />
      )}
    </div>
  );
}

export default function AdminReaders({ onCount }) {
  const [readers, setReaders] = useState([]);
  const [q, setQ] = useState("");
  const [sort, setSort] = useState("joined");
  const [open, setOpen] = useState(null);
  const [loaded, setLoaded] = useState(false);

  const load = useCallback(() => {
    api.get("/admin/readers")
      .then((r) => { setReaders(r.data.readers); onCount?.(r.data.count); setLoaded(true); })
      .catch(() => { toast.error("Could not load the readers"); setLoaded(true); });
  }, [onCount]);
  useEffect(load, [load]);

  const rows = useMemo(() => {
    const needle = q.trim().toLowerCase();
    const list = needle
      ? readers.filter((r) => `${r.name || ""} ${r.email || ""}`.toLowerCase().includes(needle))
      : [...readers];
    const weight = (r) => r.tbr_count + r.note_count + r.progress_count;
    if (sort === "active") list.sort((a, b) => (b.last_active || "").localeCompare(a.last_active || ""));
    else if (sort === "activity") list.sort((a, b) => weight(b) - weight(a));
    else list.sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""));
    return list;
  }, [readers, q, sort]);

  if (open) return <ReaderDetail userId={open} onBack={() => setOpen(null)} onChanged={load} />;

  return (
    <div data-testid="admin-readers">
      <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 border-b border-line pb-6">
        <div>
          <h2 className="font-serif text-3xl font-light text-vellum" data-testid="readers-count">
            {readers.length} {readers.length === 1 ? "reader" : "readers"}
          </h2>
          <p className="text-sm text-vellum-muted mt-2">Accounts created by signing in with Google. Read live from the database.</p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative">
            <Search className="w-3.5 h-3.5 text-vellum-faint absolute left-4 top-1/2 -translate-y-1/2" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Name or email"
              data-testid="readers-search"
              className="bg-transparent border border-line pl-10 pr-4 py-3 text-vellum font-sans text-sm focus:border-crimson focus:outline-none placeholder:text-vellum-faint w-60"
            />
          </div>
          <div className="flex divide-x divide-line border border-line">
            {SORTS.map(([k, label]) => (
              <button
                key={k}
                onClick={() => setSort(k)}
                data-testid={`readers-sort-${k}`}
                className={`${mono} px-4 py-3 transition-colors ${sort === k ? "text-vellum bg-ink-700" : "text-vellum-muted hover:text-vellum"}`}
              >
                {label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {!loaded ? (
        <p className={`${mono} text-vellum-muted mt-8`}>Loading…</p>
      ) : rows.length === 0 ? (
        <p className="text-sm text-vellum-muted mt-8" data-testid="readers-empty">
          {readers.length === 0 ? "No one has signed in yet." : "No reader matches that."}
        </p>
      ) : (
        <div className="mt-8 border border-line divide-y divide-line">
          {rows.map((r, i) => (
            <button
              key={r.user_id}
              onClick={() => setOpen(r.user_id)}
              data-testid={`reader-row-${i}`}
              className="w-full text-left px-5 py-5 grid grid-cols-1 lg:grid-cols-[1fr_auto] gap-4 items-center hover:bg-ink-800 transition-colors"
            >
              <div className="flex items-center gap-4 min-w-0">
                <Avatar reader={r} />
                <div className="min-w-0">
                  <p className="text-sm text-vellum truncate">
                    {r.name || "No name"}
                    {r.blocked && <span className={`${mono} text-crimson ml-3`} data-testid={`reader-blocked-${i}`}>Blocked</span>}
                  </p>
                  <p className="text-xs text-vellum-muted truncate">{r.email}</p>
                </div>
              </div>
              <div className={`${mono} text-vellum-muted flex flex-wrap items-center gap-x-6 gap-y-2 lg:justify-end`}>
                <span>{r.tbr_count} TBR · {r.note_count} notes · {r.progress_count} in progress</span>
                <span className="text-vellum-faint">Joined {fmtDate(r.created_at)}</span>
                <span className="text-vellum-faint">Active {fmtWhen(r.last_active)}</span>
                <span className={r.active_sessions ? "text-brass" : "text-vellum-faint"}>
                  {r.active_sessions ? `Signed in · ${r.active_sessions}` : "Signed out"}
                </span>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
